// Package-preview layout data — the data-driven replacement for the old
// 7,500-line AbsolutePosImgs.jsx. A "layout" describes how one package option's
// preview is composited: up to 16 absolutely-positioned slots, each layering a
// background, the student's pose, and a watermark.
//
// Each slot is either `null` (hidden — not rendered) or:
//   { top, left, width, pose, background }
//     top/left/width : CSS percentages, canvas-relative (resolution-independent)
//     pose           : which pose ROLE fills the slot (see POSE_ROLES below);
//                      resolved to a real image URL at render time
//     background     : a background TAG (see BG_SUBSTRINGS) or null (no bg layer)
//
// Pose roles (resolved in AbsolutePosImgs from the student's live poses):
//   pose1                  the student's first pose (deterministic)
//   pose2 / pose3 / pose4  a random "vertical" pose (for pkg 610, pose2/pose3
//                          exclude spring poses)
//   poseHoriz              a horizontal/formal pose found by URL scan
//   springpose1 / springpose2  pkg-610-only: random vertical pose excluding "-e0"
//
// These tables were generated from the legacy component and verified to
// reproduce its layout choice for all 2,322 real package options
// (see packageLayouts.test.js). Edit a layout's slots to adjust a preview.

// Background TAG -> the substring matched against the school's background set
// (getBackgroundUrl in AbsolutePosImgs scans backgrounds[].imageUrl for it).
export const BG_SUBSTRINGS = {
  blueBg: 'Senior_Blue',
  redBg: 'Senior_Red',
  whiteBg: 'Senior White',
  mochaBg: 'gradmocha',
  greenBg: 'Senior_Green',
  archwayBg: 'Archway',
  leavesBg: 'Leaves_Depth',
  brownBg: 'rown',
  grayBg: 'Gray',
  tealBg: 'Teal',
  concreteBg: 'oncrete',
  springBirch: 'irch',
  springOrchard: 'Orchard',
  fallleaves: 'all',
  chalkboardBg: 'halk2024',
  classroomBg: 'lassroom',
  flowerfieldBg: 'lowerfield',
  springstairsBg: 'springstairs',
  springarchBg: 'SpringArch',
};

export const LAYOUTS = {
  // scholarsChoice — e.g. 6 "B - Scholar" (28 options)
  scholarsChoice: [
    {
      top: '17%', left: '3%', width: '32%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '32%', left: '38%', width: '26%', pose: 'pose2', background: 'leavesBg',
    },
    {
      top: '10%', left: '67%', width: '12%', pose: 'pose3', background: 'blueBg',
    },
    {
      top: '10%', left: '88%', width: '9%', pose: 'pose4', background: 'greenBg',
    },
    {
      top: '50%', left: '67%', width: '12%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '54%', left: '86%', width: '6%', pose: 'pose2', background: 'mochaBg',
    },
    {
      top: '54%', left: '92%', width: '6%', pose: 'pose2', background: 'mochaBg',
    },
    {
      top: '66%', left: '86%', width: '6%', pose: 'pose2', background: 'mochaBg',
    },
    {
      top: '66%', left: '92%', width: '6%', pose: 'pose2', background: 'mochaBg',
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // economySelection — e.g. 532 "Economy Selection" (21 options)
  economySelection: [
    {
      top: '35%', left: '4%', width: '18%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '35%', left: '21%', width: '18%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '40%', left: '42%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '40%', left: '49%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '40%', left: '56%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '40%', left: '64%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '42%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '49%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '56%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '64%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '24%', left: '75%', width: '12%', pose: 'pose3', background: 'leavesBg',
    },
    {
      top: '24%', left: '86%', width: '12%', pose: 'pose3', background: 'leavesBg',
    },
    {
      top: '48%', left: '75%', width: '12%', pose: 'pose3', background: 'leavesBg',
    },
    {
      top: '48%', left: '86%', width: '12%', pose: 'pose3', background: 'leavesBg',
    },
    null,
    null,
  ],
  // seniorSpecial — e.g. 532 "Senior Special" (21 options)
  seniorSpecial: [
    {
      top: '7%', left: '4%', width: '25%', pose: 'pose2', background: 'concreteBg',
    },
    {
      top: '7%', left: '31%', width: '25%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '60%', left: '14%', width: '13%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '53%', left: '81%', width: '15%', pose: 'pose4', background: 'leavesBg',
    },
    {
      top: '49%', left: '63%', width: '12%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '8%', left: '60%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '8%', left: '67%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '8%', left: '74%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '8%', left: '81%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '22%', left: '60%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '22%', left: '67%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '22%', left: '74%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '22%', left: '81%', width: '7%', pose: 'pose2', background: 'redBg',
    },
    {
      top: '60%', left: '26%', width: '13%', pose: 'pose3', background: 'brownBg',
    },
    null,
    null,
  ],
  // standardPackage — e.g. 532 "Standard Package" (21 options)
  standardPackage: [
    {
      top: '27%', left: '10%', width: '24%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '12%', left: '40%', width: '16%', pose: 'pose2', background: 'grayBg',
    },
    {
      top: '12%', left: '55%', width: '16%', pose: 'pose2', background: 'grayBg',
    },
    {
      top: '50%', left: '42%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '50%', left: '49%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '50%', left: '55%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '50%', left: '62%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '64%', left: '42%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '64%', left: '49%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '64%', left: '55%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '64%', left: '62%', width: '7.5%', pose: 'pose3', background: 'brownBg',
    },
    {
      top: '30%', left: '72%', width: '12%', pose: 'pose4', background: 'leavesBg',
    },
    {
      top: '30%', left: '84%', width: '12%', pose: 'pose4', background: 'leavesBg',
    },
    {
      top: '53%', left: '72%', width: '12%', pose: 'pose4', background: 'leavesBg',
    },
    {
      top: '53%', left: '84%', width: '12%', pose: 'pose4', background: 'leavesBg',
    },
    null,
  ],
  // ultimateCollection — e.g. 532 "Ultimate Collection" (24 options)
  ultimateCollection: [
    {
      top: '16%', left: '3%', width: '32%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '42%', left: '40%', width: '20%', pose: 'pose2', background: 'grayBg',
    },
    {
      top: '42%', left: '63%', width: '8%', pose: 'pose3', background: 'redBg',
    },
    {
      top: '63%', left: '63%', width: '8%', pose: 'pose3', background: 'redBg',
    },
    {
      top: '9%', left: '69%', width: '14%', pose: 'pose4', background: 'leavesBg',
    },
    {
      top: '9%', left: '83%', width: '14%', pose: 'pose4', background: 'leavesBg',
    },
    {
      top: '55%', left: '73%', width: '10%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '53%', left: '86%', width: '6%', pose: 'pose2', background: 'greenBg',
    },
    {
      top: '53%', left: '93%', width: '6%', pose: 'pose2', background: 'greenBg',
    },
    {
      top: '69%', left: '86%', width: '6%', pose: 'pose2', background: 'greenBg',
    },
    {
      top: '69%', left: '93%', width: '6%', pose: 'pose2', background: 'greenBg',
    },
    null,
    null,
    null,
    null,
    null,
  ],
  // starStudent — e.g. 549 "All Star" (3 options)
  starStudent: [
    {
      top: '28%', left: '7%', width: '25%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '19%', left: '36%', width: '15%', pose: 'pose2', background: 'chalkboardBg',
    },
    {
      top: '19%', left: '52%', width: '15%', pose: 'pose2', background: 'chalkboardBg',
    },
    {
      top: '61%', left: '35%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '61%', left: '42%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '61%', left: '49%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '61%', left: '56%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '77%', left: '35%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '77%', left: '42%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '77%', left: '49%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '77%', left: '56%', width: '7%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '67%', left: '68%', width: '10%', pose: 'pose2', background: 'grayBg',
    },
    {
      top: '54%', left: '82%', width: '12%', pose: 'pose1', background: 'blueBg',
    },
    null,
    null,
    null,
  ],
  // p550BasicPack — e.g. 550 "Basic Pack" (1 option)
  p550BasicPack: [
    {
      top: '12%', left: '25%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '12%', left: '32%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '12%', left: '39%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '12%', left: '46%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '29%', left: '25%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '29%', left: '32%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '29%', left: '39%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '29%', left: '46%', width: '7%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '53%', left: '19%', width: '17%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '53%', left: '35%', width: '17%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '40%', left: '57%', width: '11%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '40%', left: '68%', width: '11%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '64%', left: '57%', width: '11%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '64%', left: '68%', width: '11%', pose: 'pose1', background: 'classroomBg',
    },
    null,
    null,
  ],
  // memories — e.g. 550 "Memories for Everyone" (3 options)
  memories: [
    {
      top: '19%', left: '10%', width: '15%', pose: 'pose2', background: 'fallleaves',
    },
    {
      top: '19%', left: '25%', width: '15%', pose: 'pose2', background: 'fallleaves',
    },
    {
      top: '71%', left: '6%', width: '10%', pose: 'pose1', background: 'mochaBg',
    },
    {
      top: '61%', left: '18%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '61%', left: '25%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '61%', left: '31%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '61%', left: '38%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '77%', left: '18%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '77%', left: '25%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '77%', left: '31%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '77%', left: '38%', width: '7%', pose: 'pose2', background: 'tealBg',
    },
    {
      top: '35%', left: '51%', width: '25%', pose: 'pose1', background: 'grayBg',
    },
    {
      top: '53%', left: '82%', width: '12%', pose: 'pose1', background: 'blueBg',
    },
    null,
    null,
    null,
  ],
  // justAn8x10 — e.g. 564 "Just an 8x10" (49 options)
  justAn8x10: [
    {
      top: '19%', left: '35%', width: '30%', pose: 'pose2', background: null,
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // basicPackage — e.g. 579 "Basic Package" (25 options)
  basicPackage: [
    {
      top: '29.4%', left: '14.3%', width: '21.7%', pose: 'pose1', background: null,
    },
    {
      top: '29.4%', left: '36.9%', width: '10.7%', pose: 'pose1', background: null,
    },
    {
      top: '51.3%', left: '36.9%', width: '10.7%', pose: 'pose1', background: null,
    },
    {
      top: '43.1%', left: '54.6%', width: '7.1%', pose: 'pose2', background: null,
    },
    {
      top: '43.1%', left: '62.1%', width: '7.1%', pose: 'pose2', background: null,
    },
    {
      top: '43.1%', left: '69.5%', width: '7.1%', pose: 'pose2', background: null,
    },
    {
      top: '43.1%', left: '76.8%', width: '7.1%', pose: 'pose2', background: null,
    },
    {
      top: '57.9%', left: '54.6%', width: '7.1%', pose: 'pose2', background: null,
    },
    {
      top: '57.9%', left: '62.1%', width: '7.1%', pose: 'pose2', background: null,
    },
    {
      top: '57.9%', left: '69.5%', width: '7.1%', pose: 'pose2', background: null,
    },
    {
      top: '57.9%', left: '76.8%', width: '7.1%', pose: 'pose2', background: null,
    },
    null,
    null,
    null,
    null,
    null,
  ],
  // picturePerfect — e.g. 584 "Picture Perfect Package" (24 options)
  picturePerfect: [
    {
      top: '4.8%', left: '5.1%', width: '29.2%', pose: 'pose2', background: null,
    },
    {
      top: '7.7%', left: '37%', width: '11.5%', pose: 'pose1', background: null,
    },
    {
      top: '7.7%', left: '48.6%', width: '11.5%', pose: 'pose1', background: null,
    },
    {
      top: '31%', left: '37%', width: '11.5%', pose: 'pose1', background: null,
    },
    {
      top: '31%', left: '48.6%', width: '11.5%', pose: 'pose1', background: null,
    },
    {
      top: '66.3%', left: '5.2%', width: '7%', pose: 'pose3', background: null,
    },
    {
      top: '66.3%', left: '12.8%', width: '7%', pose: 'pose3', background: null,
    },
    {
      top: '66.3%', left: '20.3%', width: '7%', pose: 'pose3', background: null,
    },
    {
      top: '66.3%', left: '27.7%', width: '7%', pose: 'pose3', background: null,
    },
    {
      top: '81%', left: '5.2%', width: '7%', pose: 'pose3', background: null,
    },
    {
      top: '81%', left: '12.8%', width: '7%', pose: 'pose3', background: null,
    },
    {
      top: '81%', left: '20.3%', width: '7%', pose: 'pose3', background: null,
    },
    {
      top: '81%', left: '27.7%', width: '7%', pose: 'pose3', background: null,
    },
    {
      top: '64%', left: '37.9%', width: '24.1%', pose: 'poseHoriz', background: null,
    },
    {
      top: '61.6%', left: '68.4%', width: '12.1%', pose: 'pose2', background: null,
    },
    null,
  ],
  // schoolDays — e.g. 584 "School Days Package" (24 options)
  schoolDays: [
    {
      top: '25.8%', left: '7.2%', width: '29.1%', pose: 'pose4', background: null,
    },
    {
      top: '43%', left: '41.9%', width: '20%', pose: 'pose2', background: null,
    },
    {
      top: '49.9%', left: '67.6%', width: '12.4%', pose: 'pose3', background: null,
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // p610BasicPack — e.g. 610 "Basic Pack" (1 option)
  p610BasicPack: [
    {
      top: '29%', left: '10%', width: '23%', pose: 'springpose1', background: 'springOrchard',
    },
    {
      top: '41%', left: '41%', width: '13%', pose: 'pose2', background: 'grayBg',
    },
    {
      top: '35%', left: '59%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '35%', left: '68%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '35%', left: '77%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '35%', left: '86%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '59%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '68%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '77%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '56%', left: '86%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // p610DigitalAllTheWay — e.g. 610 "Digital All the Way" (1 option)
  p610DigitalAllTheWay: [
    {
      top: '13%', left: '14%', width: '8%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '13%', left: '23%', width: '8%', pose: 'pose3', background: 'grayBg',
    },
    {
      top: '33%', left: '14%', width: '8%', pose: 'pose3', background: 'chalkboardBg',
    },
    {
      top: '33%', left: '23%', width: '8%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '53%', left: '14%', width: '8%', pose: 'springpose2', background: 'flowerfieldBg',
    },
    {
      top: '53%', left: '23%', width: '8%', pose: 'springpose1', background: 'springBirch',
    },
    {
      top: '73%', left: '14%', width: '8%', pose: 'springpose1', background: 'springstairsBg',
    },
    {
      top: '73%', left: '23%', width: '8%', pose: 'springpose2', background: 'springarchBg',
    },
    {
      top: '16%', left: '39%', width: '12%', pose: 'pose1', background: 'blueBg',
    },
    {
      top: '16%', left: '53%', width: '12%', pose: 'pose3', background: 'grayBg',
    },
    {
      top: '16%', left: '66%', width: '12%', pose: 'pose3', background: 'chalkboardBg',
    },
    {
      top: '16%', left: '80%', width: '12%', pose: 'pose1', background: 'classroomBg',
    },
    {
      top: '62%', left: '39%', width: '12%', pose: 'springpose2', background: 'flowerfieldBg',
    },
    {
      top: '62%', left: '53%', width: '12%', pose: 'springpose1', background: 'springBirch',
    },
    {
      top: '62%', left: '66%', width: '12%', pose: 'springpose1', background: 'springstairsBg',
    },
    {
      top: '62%', left: '80%', width: '12%', pose: 'springpose2', background: 'springarchBg',
    },
  ],
  // p610DoubleDouble — e.g. 610 "Double Double" (1 option)
  p610DoubleDouble: [
    {
      top: '11%', left: '5%', width: '32%', pose: 'pose3', background: 'blueBg',
    },
    {
      top: '15%', left: '39%', width: '32%', pose: 'springpose1', background: 'springOrchard',
    },
    {
      top: '19%', left: '80%', width: '9%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '41%', left: '80%', width: '9%', pose: 'springpose2', background: 'springOrchard',
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // p610JustADigital — e.g. 610 "Just a Digital" (1 option)
  p610JustADigital: [
    {
      top: '28%', left: '12%', width: '20%', pose: 'pose2', background: 'blueBg',
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // p610PerfectPack2Digitals — e.g. 610 "Perfect Pack with 2 Digitals" (1 option)
  p610PerfectPack2Digitals: [
    {
      top: '13%', left: '6%', width: '35%', pose: 'pose3', background: 'blueBg',
    },
    {
      top: '7%', left: '44%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '7%', left: '52%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '7%', left: '60%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '7%', left: '68%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '44%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '52%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '60%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '68%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '13%', left: '82%', width: '13%', pose: 'springpose1', background: 'springOrchard',
    },
    {
      top: '49%', left: '47%', width: '7%', pose: 'pose3', background: 'blueBg',
    },
    {
      top: '67%', left: '47%', width: '7%', pose: 'springpose1', background: 'springOrchard',
    },
    {
      top: '49%', left: '64%', width: '15%', pose: 'springpose2', background: 'springOrchard',
    },
    {
      top: '49%', left: '77%', width: '15%', pose: 'springpose2', background: 'springOrchard',
    },
    null,
    null,
  ],
  // p610SomethingForEveryone — e.g. 610 "Something for Everyone" (1 option)
  p610SomethingForEveryone: [
    {
      top: '15%', left: '6%', width: '34%', pose: 'springpose1', background: 'springOrchard',
    },
    {
      top: '7%', left: '47%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '7%', left: '54%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '7%', left: '61%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '7%', left: '68%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '47%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '54%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '61%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '25%', left: '68%', width: '8%', pose: 'pose2', background: 'blueBg',
    },
    {
      top: '18%', left: '84%', width: '12%', pose: 'springpose2', background: 'springOrchard',
    },
    {
      top: '55%', left: '46%', width: '4%', pose: 'pose3', background: 'blueBg',
    },
    {
      top: '55%', left: '51%', width: '4%', pose: 'pose3', background: 'blueBg',
    },
    {
      top: '66%', left: '46%', width: '4%', pose: 'springpose2', background: 'springOrchard',
    },
    {
      top: '66%', left: '51%', width: '4%', pose: 'springpose2', background: 'springOrchard',
    },
    {
      top: '52%', left: '63%', width: '15%', pose: 'pose3', background: 'blueBg',
    },
    {
      top: '52%', left: '79%', width: '15%', pose: 'pose3', background: 'blueBg',
    },
  ],
  // p611Two8x10Plus — e.g. 611 "Two 8x10’s Plus" (1 option)
  p611Two8x10Plus: [
    {
      top: '13%', left: '33%', width: '34%', pose: 'pose1', background: 'blueBg',
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // p616Two8x10Plus — e.g. 616 "Two 8x10’s Plus" (1 option)
  p616Two8x10Plus: [
    {
      top: '12%', left: '33%', width: '35%', pose: 'pose1', background: 'blueBg',
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // p629DigitalAllTheWay — e.g. 629 "Digital All the Way" (1 option)
  p629DigitalAllTheWay: [
    {
      top: '32%', left: '6%', width: '19%', pose: 'pose2', background: null,
    },
    {
      top: '11%', left: '32%', width: '38%', pose: 'pose1', background: null,
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // p629Memories — e.g. 629 "Memories Package" (1 option)
  p629Memories: [
    {
      top: '12%', left: '3%', width: '23%', pose: 'pose2', background: null,
    },
    {
      top: '37%', left: '27%', width: '23%', pose: 'pose2', background: null,
    },
    {
      top: '16%', left: '52%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '16%', left: '60%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '34%', left: '52%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '34%', left: '60%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '55%', left: '52%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '55%', left: '60%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '73%', left: '52%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '73%', left: '60%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '13%', left: '69%', width: '28%', pose: 'pose1', background: null,
    },
    {
      top: '60%', left: '74%', width: '10%', pose: 'pose1', background: null,
    },
    {
      top: '60%', left: '83%', width: '10%', pose: 'pose1', background: null,
    },
    {
      top: '74%', left: '74%', width: '10%', pose: 'pose1', background: null,
    },
    {
      top: '74%', left: '83%', width: '10%', pose: 'pose1', background: null,
    },
    null,
  ],
  // p629StarStudent — e.g. 629 "Star Student Package" (1 option)
  p629StarStudent: [
    {
      top: '12%', left: '6%', width: '25%', pose: 'pose2', background: null,
    },
    {
      top: '77%', left: '7%', width: '9%', pose: 'pose2', background: null,
    },
    {
      top: '77%', left: '20%', width: '9%', pose: 'pose2', background: null,
    },
    {
      top: '11%', left: '35%', width: '14%', pose: 'pose2', background: null,
    },
    {
      top: '11%', left: '49%', width: '14%', pose: 'pose2', background: null,
    },
    {
      top: '50%', left: '35%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '50%', left: '41%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '50%', left: '48%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '50%', left: '55%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '66%', left: '35%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '66%', left: '41%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '66%', left: '48%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '66%', left: '55%', width: '7%', pose: 'pose2', background: null,
    },
    {
      top: '8%', left: '65%', width: '31%', pose: 'pose1', background: null,
    },
    {
      top: '68%', left: '66%', width: '11%', pose: 'pose1', background: null,
    },
    {
      top: '59%', left: '79%', width: '18%', pose: 'pose1', background: null,
    },
  ],
  // allDigital — e.g. 642 "All Digital Package" (15 options)
  allDigital: [
    {
      top: '10.7%', left: '6.1%', width: '17%', pose: 'pose4', background: null,
    },
    {
      top: '10.7%', left: '25%', width: '17%', pose: 'pose2', background: null,
    },
    {
      top: '48.1%', left: '6.1%', width: '21%', pose: 'poseHoriz', background: null,
    },
    {
      top: '48.1%', left: '29%', width: '13.2%', pose: 'pose3', background: null,
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // giftsForEveryone — e.g. 642 "Gifts for Everyone Package" (16 options)
  giftsForEveryone: [
    {
      top: '10.4%', left: '10.3%', width: '18%', pose: 'pose4', background: null,
    },
    {
      top: '10.4%', left: '28.5%', width: '18%', pose: 'pose4', background: null,
    },
    {
      top: '8.5%', left: '56.5%', width: '13%', pose: 'pose2', background: null,
    },
    {
      top: '7.8%', left: '76%', width: '13%', pose: 'pose2', background: null,
    },
    {
      top: '53.6%', left: '10.1%', width: '18%', pose: 'pose3', background: null,
    },
    {
      top: '53.6%', left: '28.5%', width: '18%', pose: 'pose3', background: null,
    },
    {
      top: '41.8%', left: '55%', width: '16.5%', pose: 'pose4', background: null,
    },
    {
      top: '50.1%', left: '73.4%', width: '16.5%', pose: 'pose4', background: null,
    },
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
  // keepsake — e.g. 642 "The Keepsake Collection" (16 options)
  keepsake: [
    {
      top: '13.4%', left: '8.2%', width: '22.9%', pose: 'pose4', background: null,
    },
    {
      top: '14.5%', left: '41.0%', width: '13%', pose: 'pose2', background: null,
    },
    {
      top: '44.8%', left: '35%', width: '9.2%', pose: 'pose3', background: null,
    },
    {
      top: '45.3%', left: '46.6%', width: '9.2%', pose: 'pose3', background: null,
    },
    {
      top: '44.8%', left: '61.4%', width: '15.5%', pose: 'pose4', background: null,
    },
    {
      top: '52.6%', left: '78.4%', width: '15.5%', pose: 'pose4', background: null,
    },
    {
      top: '70%', left: '4.9%', width: '8.1%', pose: 'pose3', background: null,
    },
    {
      top: '72%', left: '33.2%', width: '5.2%', pose: 'pose2', background: null,
    },
    {
      top: '72%', left: '38.6%', width: '5.2%', pose: 'pose2', background: null,
    },
    {
      top: '72%', left: '44.4%', width: '5.2%', pose: 'pose2', background: null,
    },
    {
      top: '72%', left: '50.1%', width: '5.2%', pose: 'pose2', background: null,
    },
    {
      top: '83.1%', left: '33.2%', width: '5.2%', pose: 'pose2', background: null,
    },
    {
      top: '83.1%', left: '38.6%', width: '5.2%', pose: 'pose2', background: null,
    },
    {
      top: '83.1%', left: '44.4%', width: '5.2%', pose: 'pose2', background: null,
    },
    {
      top: '83.1%', left: '50.1%', width: '5.2%', pose: 'pose2', background: null,
    },
    null,
  ],
};

// Choose the layout for a package option. Ordered, first-match-wins — mirrors the
// legacy if-chain precedence. Package-specific rules come before the general
// name-based ones (e.g. 629 and 610 reuse common option names with their own
// layouts). Returns a key of LAYOUTS, or null when the option has no preview
// layout (the preview simply isn't shown). Verified against all real options in
// packageLayouts.test.js — keep that test green when editing these rules.
export function selectLayout(packageId, optionName) {
  const pkg = Number(packageId); // packageId may arrive as a string; legacy used loose ==
  const name = optionName || '';
  const has = (s) => name.includes(s);

  // Package 610 — its own set of named layouts
  if (pkg === 610) {
    if (has('Basic Pack')) return 'p610BasicPack';
    if (has('Digital All the Way')) return 'p610DigitalAllTheWay';
    if (has('Double Double')) return 'p610DoubleDouble';
    if (has('Just a Digital')) return 'p610JustADigital';
    if (has('Perfect Pack with 2 Digitals')) return 'p610PerfectPack2Digitals';
    if (has('Something for Everyone')) return 'p610SomethingForEveryone';
    // else falls through to the general rules (e.g. 610 "Just an 8x10")
  }

  // Package 629 — reuses common names with its own layouts
  if (pkg === 629) {
    if (has('Digital All the Way')) return 'p629DigitalAllTheWay';
    if (has('Memories')) return 'p629Memories';
    if (has('Star Student')) return 'p629StarStudent';
  }

  // Package-scoped layouts that share a name with the general ones
  if (pkg === 611 && has('Two 8x10')) return 'p611Two8x10Plus';
  if (pkg === 616 && has('Two 8x10')) return 'p616Two8x10Plus';
  if (pkg === 550 && has('Basic Pack')) return 'p550BasicPack';
  if ([549, 611, 616].includes(pkg) && has('Star')) return 'starStudent';
  if ([550, 611, 616].includes(pkg) && has('Memories')) return 'memories';

  // General, name-based layouts (package-independent)
  if (has('Scholar')) return 'scholarsChoice';
  if (has('Economy Selection')) return 'economySelection';
  if (has('Senior Special')) return 'seniorSpecial';
  if (has('Standard Package')) return 'standardPackage';
  if (has('Ultimate')) return 'ultimateCollection';
  if (has('Gifts for Everyone Package')) return 'giftsForEveryone';
  if (has('The Keepsake Collection')) return 'keepsake';
  if (has('All Digital Package')) return 'allDigital';
  if (has('Picture Perfect Package')) return 'picturePerfect';
  if (has('School Days Package')) return 'schoolDays';
  if (has('Just an 8x10')) return 'justAn8x10';
  if (has('Basic Package')) return 'basicPackage';

  return null;
}
